import {
  WORKER_PROTOCOL,
  WorkerJobError,
  createAbortError,
  type WorkerEventMessage,
  type WorkerProgress,
  type WorkerStartMessage,
} from './protocol'

export interface RunWorkerJobOptions<Input, Result> {
  createWorker: () => Worker
  input: Input
  validate: (input: Input) => void
  validateResult: (value: unknown) => value is Result
  signal?: AbortSignal
  onProgress?: (progress: WorkerProgress) => void
  transfer?: (input: Input) => Transferable[]
  timeoutMs?: number
  timeoutCode?: string
}

let nextJobNumber = 0

export async function runWorkerJob<Input, Result>({
  createWorker,
  input,
  validate,
  validateResult,
  signal,
  onProgress,
  transfer,
  timeoutMs,
  timeoutCode,
}: RunWorkerJobOptions<Input, Result>): Promise<Result> {
  if (signal?.aborted) throw createAbortError()

  try {
    const validationResult: unknown = validate(input)
    if (isThenable(validationResult)) {
      void Promise.resolve(validationResult).catch(() => {})
      throw new Error('Async validation is not supported.')
    }
  } catch (error) {
    if (error instanceof WorkerJobError) throw error
    throw new WorkerJobError('recoverable', 'validation_failed')
  }

  if (signal?.aborted) throw createAbortError()

  let worker: Worker
  try {
    worker = createWorker()
  } catch {
    throw new WorkerJobError('fatal', 'worker_unavailable')
  }
  if (signal?.aborted) {
    worker.terminate()
    throw createAbortError()
  }

  nextJobNumber += 1
  const jobId = `job-${nextJobNumber}`

  return new Promise<Result>((resolve, reject) => {
    let settled = false
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    const cleanup = () => {
      worker.removeEventListener('message', handleMessage)
      worker.removeEventListener('error', handleWorkerFailure)
      worker.removeEventListener('messageerror', handleMessageFailure)
      signal?.removeEventListener('abort', handleAbort)
      if (timeoutId !== undefined) clearTimeout(timeoutId)
      worker.terminate()
    }

    const settle = (complete: () => void) => {
      if (settled) return
      settled = true
      cleanup()
      complete()
    }

    const failFatal = (code: string) => {
      settle(() => reject(new WorkerJobError('fatal', code)))
    }

    const handleMessage = (event: MessageEvent<unknown>) => {
      const value = event.data
      if (!isObject(value)) {
        failFatal('invalid_message')
        return
      }
      if (typeof value.jobId !== 'string') {
        failFatal('invalid_message')
        return
      }
      if (value.jobId !== jobId) return
      if (value.protocol !== WORKER_PROTOCOL) {
        failFatal('protocol_mismatch')
        return
      }

      const message = value as Partial<WorkerEventMessage<Result>>
      if (message.type === 'progress' && isProgress(message.progress)) {
        try {
          onProgress?.(message.progress)
        } catch {
          failFatal('progress_callback_failed')
        }
        return
      }
      if (message.type === 'result' && 'result' in message) {
        let validResult = false
        try {
          validResult = validateResult(message.result)
        } catch {
          failFatal('invalid_message')
          return
        }
        if (!validResult) {
          failFatal('invalid_message')
          return
        }
        settle(() => resolve(message.result as Result))
        return
      }
      if (message.type === 'error' && isErrorPayload(message.error)) {
        const workerError = message.error
        settle(() => reject(new WorkerJobError(workerError.kind, workerError.code)))
        return
      }
      failFatal('invalid_message')
    }

    const handleWorkerFailure = (event: ErrorEvent) => {
      event.preventDefault()
      failFatal('worker_failed')
    }

    const handleMessageFailure = () => failFatal('message_failed')
    const handleAbort = () => settle(() => reject(createAbortError()))

    worker.addEventListener('message', handleMessage)
    worker.addEventListener('error', handleWorkerFailure)
    worker.addEventListener('messageerror', handleMessageFailure)
    signal?.addEventListener('abort', handleAbort, { once: true })
    if (timeoutMs !== undefined) {
      if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
        failFatal(timeoutCode ?? 'worker_timeout')
        return
      }
      timeoutId = setTimeout(() => failFatal(timeoutCode ?? 'worker_timeout'), timeoutMs)
    }

    const message: WorkerStartMessage<Input> = {
      protocol: WORKER_PROTOCOL,
      type: 'start',
      jobId,
      input,
    }

    try {
      worker.postMessage(message, transfer?.(input) ?? [])
    } catch {
      failFatal('message_failed')
    }
  })
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isThenable(value: unknown): value is PromiseLike<unknown> {
  return isObject(value) && typeof value.then === 'function'
}

function isProgress(value: unknown): value is WorkerProgress {
  if (!isObject(value)) return false
  if (value.unit !== 'bytes' && value.unit !== 'items' && value.unit !== 'percent') return false
  if (typeof value.completed !== 'number' || !Number.isFinite(value.completed) || value.completed < 0) return false
  if (value.total !== undefined && (
    typeof value.total !== 'number'
    || !Number.isFinite(value.total)
    || value.total < value.completed
  )) return false
  return value.stage === undefined || typeof value.stage === 'string'
}

function isErrorPayload(value: unknown): value is { kind: 'recoverable' | 'fatal'; code: string } {
  return isObject(value)
    && (value.kind === 'recoverable' || value.kind === 'fatal')
    && typeof value.code === 'string'
    && value.code.length > 0
}
